import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getPest } from "@/data/pests";

// Lead capture on guide pages.
//
// Guides are long — the mice guide runs ~14 screens on a phone — and before
// this the only conversion point sat 9 screens down. Two surfaces fix that
// without turning the page into a sales pitch:
//
//   inline  — a static card placed early in the article body. Real markup in
//             the prerendered HTML, so crawlers and AI readers see the offer
//             and the internal link.
//   sticky  — a slim fixed bar that appears once the reader is past the hero
//             and retreats before the footer CTA so they never double up.
//
// Deliberately NOT a popup or interstitial. Google treats intrusive mobile
// interstitials as a ranking negative, and "we interrupt you to sell" is the
// opposite of the brand's whole position.

// A guide tagged with a pest deep-links to that pest's funnel, which
// preselects step 1 of the form. Untagged guides go to the generic page.
function ctaTarget(pest) {
  return pest ? `/hire-a-pro/${pest}` : "/hire-a-pro";
}

function pestLabel(pest) {
  const p = pest ? getPest(pest) : null;
  return p ? p.name.toLowerCase() : null;
}

export function InlineCTA({ pest }) {
  const label = pestLabel(pest);
  return (
    <aside
      className="my-10 rounded-[1.75rem] border border-forest/12 bg-sand/70 p-6 sm:p-7"
      data-testid="guide-inline-cta"
    >
      <p className="font-serif text-xl sm:text-2xl font-bold text-forest-950 leading-snug">
        {label ? `Dealing with ${label} right now?` : "Dealing with this right now?"}
      </p>
      <p className="mt-2.5 text-[16px] leading-relaxed text-forest-950/75">
        Tell us what you&rsquo;re seeing and we&rsquo;ll connect you with a local
        provider{label ? ` who handles ${label}` : ""}. Takes about a minute, and
        it&rsquo;s free.
      </p>
      <Link
        to={ctaTarget(pest)}
        className="mt-5 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-terracotta px-7 text-white font-semibold transition-colors hover:bg-terracotta/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terracotta"
        data-testid="guide-inline-cta-btn"
      >
        Find a pro near me <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="mt-3 text-sm text-forest-950/55">
        No obligation. We&rsquo;ll also tell you when you don&rsquo;t need one.
      </p>
    </aside>
  );
}

export function StickyCTA({ pest }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Show once they're past the hero; hide near the bottom so this never
    // sits on top of the end-of-article CTA or the footer.
    const onScroll = () => {
      const y = window.scrollY;
      const fromBottom =
        document.body.scrollHeight - (y + window.innerHeight);
      setShow(y > 900 && fromBottom > 1400);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const label = pestLabel(pest);

  return (
    <div
      aria-hidden={!show}
      className={`fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ${
        show ? "translate-y-0" : "translate-y-full"
      }`}
      data-testid="guide-sticky-cta"
    >
      <div className="mx-auto max-w-3xl px-3 pb-3">
        <div className="flex items-center gap-3 rounded-2xl bg-forest-950/95 backdrop-blur px-4 py-3 shadow-[0_8px_30px_rgba(18,35,24,0.28)]">
          <p className="min-w-0 flex-1 text-cream text-[15px] leading-snug">
            <span className="font-semibold">
              {label ? `Need a pro for ${label}?` : "Need a pro?"}
            </span>{" "}
            <span className="text-cream/70 hidden sm:inline">
              Free, about a minute.
            </span>
          </p>
          <Link
            to={ctaTarget(pest)}
            tabIndex={show ? 0 : -1}
            className="shrink-0 inline-flex min-h-[44px] items-center rounded-full bg-terracotta px-5 text-white text-sm font-semibold transition-colors hover:bg-terracotta/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cream"
            data-testid="guide-sticky-cta-btn"
          >
            Get matched
          </Link>
        </div>
      </div>
    </div>
  );
}
